const styles = ':host{--modal-body-padding-inline:1.5rem}.modal-body{flex-grow:1;overflow-x:hidden;overflow-y:auto;padding:0 var(--modal-body-padding-inline,1.5rem)}.modal-body::slotted(skapa-modal-edge){display:block;margin-left:calc(var(--modal-body-padding-inline, 1.5rem)*-1);margin-right:calc(var(--modal-body-padding-inline, 1.5rem)*-1);width:calc(100% + var(--modal-body-padding-inline, 1.5rem)*2)}@media (min-width:37.5em){:host(:not([size=small])){--modal-body-padding-inline:2rem}}@media (min-width:75em){:host .modal-body{--modal-body-padding-inline:2.5rem}}:host{--skapa-modal-transition-duration:100ms;--skapa-modal-transition-function:cubic-bezier(0.9,0,1,1);bottom:0;left:0;position:fixed;right:0;top:0;transition:visibility var(--skapa-modal-transition-duration) step-end;visibility:hidden;z-index:3}:host([open]){--skapa-modal-transition-duration:200ms;--skapa-modal-transition-function:cubic-bezier(0.8,0,0,1);pointer-events:unset;transition-timing-function:step-start;visibility:visible}:host([open]) .backdrop{opacity:1}.backdrop{background:rgba(var(--colour-static-black,17,17,17),.4);bottom:0;left:0;opacity:0;position:absolute;right:0;top:0;transition:opacity var(--skapa-modal-transition-duration) var(--skapa-modal-transition-function)}*{box-sizing:border-box}slot{display:block}.modal{background:rgb(var(--colour-elevation-2,255,255,255));display:none;flex-basis:auto;flex-direction:column;min-height:0;overflow:hidden;visibility:hidden;z-index:1}.modal.visible,:host([open]) .modal{display:flex}.modal.visible{visibility:visible}slot[name=header]{z-index:1}@media (prefers-reduced-motion){:host{--skapa-modal-transition-duration:1ms!important}}';
import { SkapaElement } from "@ingka/skapa-webc-element";
import { lockBody, releaseBody } from "./utils/scroll-lock.js";
import { SkapaFocusLock } from "@ingka/focus-lock-shared";
function generateTemplate({ html }) {
  const template = document.createElement("template");
  template.innerHTML = html`
    <style>
      ${styles}
    </style>
    <div class="backdrop"></div>
  `;
  return { base: template };
}
class ModalAbstract extends SkapaElement {
  constructor() {
    super();
    this.__panel = null;
    this.reportCloseAnimationFinished = (ev) => {
      if (!this.open && ev.target === this) {
        cancelAnimationFrame(this.__animationHelperID);
        this.panel.classList.remove("visible");
        this.dispatchEvent(new Event("closed"));
      }
    };
    this.handleEscapeRequest = () => {
      const closeEvent = new CustomEvent("closerequest");
      this.dispatchEvent(closeEvent);
      if (!closeEvent.defaultPrevented) this.open = false;
    };
    this.__animationHelperID = -1;
    this.updateAssitiveTools = () => {
      cancelAnimationFrame(this.__animationHelperID);
      this.focusTrap.autoFocusReturn = !this.noAutoReturn;
      if (this.open && this.isConnected) {
        const initialTransform = window.getComputedStyle(this).transform;
        const lockAndAnimateDialog = () => {
          this.panel.classList.add("visible");
          this.enableFocuslock();
          if (!this.unlockedScroll) lockBody(this);
          else releaseBody(this);
        };
        if (this.freeFocus) this.focusTrap.active = false;
        this.__animationHelperID = requestAnimationFrame(lockAndAnimateDialog);
      } else {
        releaseBody(this);
        this.disableFocusLock();
      }
    };
    this.gatherProperties(
      ["open", "freeFocus", "unlockedScroll", "noAutoReturn"],
      this
    );
    const shadow = this.attachShadow({ mode: "open" });
    const template = this.getTemplates(generateTemplate, "modal-abstract");
    shadow.append(template.base.content.cloneNode(true));
    this.backdrop = shadow.querySelector("div.backdrop");
    this.focusTrap = new SkapaFocusLock(this);
    this.focusTrap.autoFocus = true;
    this.focusTrap.capturePointerEvents = false;
  }
  get panel() {
    if (!this.__panel)
      this.__panel = this.shadowRoot.querySelector(
        "div.modal"
      );
    return this.__panel;
  }
  connectedCallback() {
    super.connectedCallback();
    this.setAttribute("aria-hidden", (!this.open).toString());
    this.backdrop.addEventListener("click", this.handleEscapeRequest);
    this.addEventListener("transitionend", this.reportCloseAnimationFinished);
    this.updateAssitiveTools();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.backdrop.removeEventListener("click", this.handleEscapeRequest);
    this.updateAssitiveTools();
    this.removeEventListener(
      "transitionend",
      this.reportCloseAnimationFinished
    );
  }
  get open() {
    return this.hasAttribute("open");
  }
  set open(val) {
    this.toggleAttribute("open", !!val);
  }
  get freeFocus() {
    return this.hasAttribute("free-focus");
  }
  set freeFocus(toggle) {
    this.toggleAttribute("free-focus", !!toggle);
  }
  get noAutoReturn() {
    return this.hasAttribute("no-auto-return");
  }
  set noAutoReturn(toggle) {
    this.toggleAttribute("no-auto-return", !!toggle);
  }
  get unlockedScroll() {
    return this.hasAttribute("unlocked-scroll");
  }
  set unlockedScroll(toggle) {
    this.toggleAttribute("unlocked-scroll", !!toggle);
  }
  enableFocuslock() {
    this.focusTrap.active = !this.freeFocus;
    this.focusTrap.removeEventListener(
      "skapa-focus-lock-escape-hotkey",
      this.handleEscapeRequest
    );
    this.focusTrap.addEventListener(
      "skapa-focus-lock-escape-hotkey",
      this.handleEscapeRequest
    );
  }
  disableFocusLock() {
    this.focusTrap.active = false;
    this.focusTrap.removeEventListener(
      "skapa-focus-lock-escape-hotkey",
      this.handleEscapeRequest
    );
  }
  attributeChangedCallback(name, oldVal, newVal) {
    if (name === "open") {
      const isOpen = newVal !== null;
      this.setAttribute("role", isOpen ? "dialog" : "none");
      this.setAttribute("aria-modal", isOpen.toString());
      this.setAttribute("aria-hidden", (!isOpen).toString());
      this.updateAssitiveTools();
    } else if (["no-auto-return", "free-focus", "unlocked-scroll"].includes(name)) {
      this.updateAssitiveTools();
    }
  }
}
ModalAbstract.skapaDependencies = [];
ModalAbstract.observedAttributes = [
  "open",
  "free-focus",
  "unlocked-scroll",
  "no-auto-return"
];
export {
  ModalAbstract
};
